import React, {
  useEffect,
  useState,
  useContext,
  useMemo,
  useCallback,
} from "react";
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  IconButton,
  Pagination,
  Box,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import Swal from "sweetalert2";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useNavigate } from "react-router-dom";
import { UserContext } from "App";
import AddmissionButton from "layouts/AccountingSessions/AddmissionButton";
import PayFromBalanceButton from "layouts/AccountingSessions/PayFromBalanceButton";

const IMPLEMENTED_TYPES = ["Stay", "Package"];

const SessionsTable = ({
  accountingSessions,
  setIsLoaded,
  refundSession,
  AbsentButtonClick,
  DeleteSessions,
  unabsentAndRefundSessions,
  type,
}) => {
  if (!IMPLEMENTED_TYPES.includes(type))
    throw new Error(`Type ${type} not implemented yet in SessionsTable.`);

  const CurrentUser = useContext(UserContext);
  let navigate = useNavigate();

  const [selectedSessions, setSelectedSessions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const sortedSessions = useMemo(() => {
    return [...accountingSessions].sort(
      (a, b) => new Date(b.appointmentTime) - new Date(a.appointmentTime),
    );
  }, [accountingSessions]);

  const paginatedSessions = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return sortedSessions.slice(start, end);
  }, [sortedSessions, currentPage]);

  const totalPages = Math.ceil(sortedSessions.length / rowsPerPage);

  const handleSelect = useCallback((session, checked) => {
    setSelectedSessions((prev) =>
      checked ? [...prev, session] : prev.filter((s) => s.id !== session.id),
    );
  }, []);

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedSessions(sortedSessions);
    } else {
      setSelectedSessions([]);
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleDeleteUnadmittedUnpaidSessions = () => {
    const sessions = accountingSessions.filter(
      (session) => !session.isPaidFor && !session.isAdmitted,
    );
    DeleteSessions(sessions);
  };

  const handleDetails = (session) => {
    if (
      ![
        "Admin", "Owner", "HeadAccounting",
        "Therapist",
        "AccountingEmployee",
        "AppointmentEmployee",
        "CallCenterEmployee",
      ].includes(CurrentUser.userType)
    ) {
      Swal.fire({
        title: "Access Denied",
        text: "You do not have permission to view this session details.",
        icon: "error",
        confirmButtonText: "Ok",
      });
      return;
    }
    navigate("/AccountingSessions/Details", {
      state: { SearchSession: session },
    });
  };

  const allSelected =
    sortedSessions.length > 0 &&
    sortedSessions.every((session) =>
      selectedSessions.some((s) => s.id === session.id),
    );

  const someSelected =
    sortedSessions.length > 0 &&
    sortedSessions.some((session) =>
      selectedSessions.some((s) => s.id === session.id),
    ) &&
    !allSelected;

  return (
    <Card sx={{ mb: 5, overflow: "visible" }}>
      <MDBox
        mt={-3}
        py={3}
        px={2}
        variant="gradient"
        coloredShadow="info"
        sx={{
          borderTopLeftRadius: "1.1rem",
          borderTopRightRadius: "1.1rem",
          backgroundColor: "#3560AD",
        }}
      >
        <MDTypography variant="h6" color="white">
          Accounting Sessions Table
        </MDTypography>
        <>
          <button
            className="btn btn-danger"
            style={{
              backgroundColor: "#4b4848ff",
              color: "white",
              border: "none",
              float: "right",
              marginLeft: "10px",
            }}
            onClick={() => {
              handleDeleteUnadmittedUnpaidSessions();
            }}
          >
            Delete Unadmitted & Unpaid
          </button>
          {selectedSessions.length > 0 && (
            <>
              <button
                className="btn btn-danger"
                style={{ float: "right", marginLeft: "10px" }}
                onClick={async () => {
                  await DeleteSessions(selectedSessions);
                  setSelectedSessions([]);
                }}
              >
                Delete ({selectedSessions.length})
              </button>

              <button
                className="btn btn-success"
                style={{ float: "right", marginLeft: "10px" }}
                onClick={async () => {
                  await refundSession(selectedSessions);
                  setSelectedSessions([]);
                }}
              >
                Refund ({selectedSessions.length})
              </button>

              <button
                className="btn"
                style={{
                  backgroundColor: "#ff8800",
                  color: "white",
                  border: "none",
                  float: "right",
                  marginLeft: "10px",
                }}
                onClick={async () => {
                  await AbsentButtonClick(selectedSessions);
                  setSelectedSessions([]);
                }}
              >
                Unabsent ({selectedSessions.length})
              </button>
              <button
                className="btn"
                style={{
                  backgroundColor: "#7e2323ff",
                  color: "white",
                  border: "none",
                  float: "right",
                  marginLeft: "10px",
                }}
                onClick={async () => {
                  await unabsentAndRefundSessions(selectedSessions);
                  setSelectedSessions([]);
                }}
              >
                Unabsent & Refund ({selectedSessions.length})
              </button>
            </>
          )}
        </>
      </MDBox>

      <TableContainer>
        <Table>
          <TableHead sx={{ display: "table-header-group" }}>
            <TableRow>
              <TableCell padding="checkbox" align="center" sx={{ width: 60 }}>
                <Checkbox
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  checked={allSelected}
                  indeterminate={someSelected}
                />
              </TableCell>
              <TableCell sx={{ minWidth: 150 }}>Session</TableCell>
              <TableCell sx={{ minWidth: 120 }}>Therapist</TableCell>
              <TableCell sx={{ minWidth: 150 }}>Appointment</TableCell>
              <TableCell sx={{ minWidth: 120 }}>Addmission</TableCell>
              {type === "Package" && (
                <TableCell sx={{ minWidth: 150 }}>Pay/Refund</TableCell>
              )}
              <TableCell align="center" sx={{ width: 100 }}>
                Details
              </TableCell>
              <TableCell align="center" sx={{ width: 100 }}>
                Delete
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedSessions.map((session) => (
              <TableRow key={session.id} hover>
                <TableCell padding="checkbox" align="center">
                  <Checkbox
                    checked={selectedSessions.some((s) => s.id === session.id)}
                    onChange={(e) => handleSelect(session, e.target.checked)}
                  />
                </TableCell>

                <TableCell>
                  <MDTypography
                    variant="button"
                    color="text"
                    fontWeight="medium"
                    noWrap
                  >
                    {session.id} - {session.sessionName}
                  </MDTypography>
                </TableCell>

                <TableCell>
                  <MDTypography
                    variant="button"
                    color="text"
                    fontWeight="medium"
                    noWrap
                  >
                    {session.therapistName}
                  </MDTypography>
                </TableCell>

                <TableCell>
                  <MDTypography
                    variant="button"
                    color="text"
                    fontWeight="medium"
                    noWrap
                  >
                    {session.appointmentTime}
                  </MDTypography>
                </TableCell>

                <TableCell>
                  <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                    <AddmissionButton
                      AccSession={session}
                      setIsLoaded={setIsLoaded}
                    />
                  </Box>
                </TableCell>

                {type === "Package" && (
                  <TableCell>
                    <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                      {session.isPaidFor ? (
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={() => refundSession(session)}
                        >
                          refund {session.finalPrice} EGP
                        </Button>
                      ) : (
                        <PayFromBalanceButton
                          Session={session}
                          setIsLoaded={setIsLoaded}
                        />
                      )}
                    </Box>
                  </TableCell>
                )}

                <TableCell align="center">
                  <IconButton
                    color="primary"
                    onClick={() => handleDetails(session)}
                  >
                    <Icon>settings</Icon>
                  </IconButton>
                </TableCell>

                <TableCell align="center">
                  <IconButton
                    color="error"
                    onClick={() => DeleteSessions(session)}
                  >
                    <Icon>delete</Icon>
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}

            {paginatedSessions.length === 0 && (
              <TableRow>
                <TableCell colSpan={type === "Package" ? 8 : 7} align="center">
                  <MDTypography variant="button" color="text">
                    No sessions found.
                  </MDTypography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <MDBox display="flex" justifyContent="center" py={3}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
          />
        </MDBox>
      )}
    </Card>
  );
};

export default SessionsTable;
